import { useMemo } from "react";
import { useSelector } from "react-redux";
import { CopyResourceResultDc, ServiceInfoDc } from "@evergis/api";

import { getProjectLayers } from "../../../../ducks/project/selectors";
import { getLayerInfo } from "../../../../evergis/core/layer/getLayerInfo";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";
import { LayerInfo } from "../../../../interfaces/resourceInfo";

export const useCopyResults = (jobResult: CopyResourceResultDc[], filteredLayers: ServiceInfoDc[]) => {
  const defaultScaleRange = useDefaultScaleRange();
  const layerInfos = useSelector(getProjectLayers);
  const filteredLayerInfos: LayerInfo[] = useMemo(() => {
    return filteredLayers ? filteredLayers.map(layer => getLayerInfo(layer, defaultScaleRange)) : [];
  }, [defaultScaleRange, filteredLayers]);

  const filteredLayersTables: string[] = useMemo(
    () => filteredLayerInfos.map(layerInfo => layerInfo.tableName),
    [filteredLayerInfos],
  );

  const layersToAddToProject: LayerInfo[] = useMemo(
    () =>
      getItemsCopied(jobResult, "layer").map(layer =>
        getLayerInfo({ ...layerInfos[layer.name], name: layer.copyName }, defaultScaleRange),
      ),
    [defaultScaleRange, jobResult, layerInfos],
  );

  const filteredLayersNames: string[] = useMemo(
    () => filteredLayerInfos.map(layer => layer.name),
    [filteredLayerInfos],
  );

  const projectsCopied: CopyResourceResultDc[] = useMemo(() => getItemsCopied(jobResult, "project"), [jobResult]);

  const layersCopied: CopyResourceResultDc[] = useMemo(() => getItemsCopied(jobResult, "layer"), [jobResult]);

  const tablesCopied: CopyResourceResultDc[] = useMemo(() => getItemsCopied(jobResult, "table"), [jobResult]);

  return {
    filteredLayersTables,
    layersToAddToProject,
    filteredLayersNames,
    projectsCopied,
    layersCopied,
    tablesCopied,
    filteredLayerInfos,
  } as const;
};

function getItemsCopied(
  jobResult: CopyResourceResultDc[],
  itemType: "table" | "layer" | "project",
): CopyResourceResultDc[] {
  return jobResult
    ? jobResult.filter(item => {
        return item.type === itemType && item.isSuccess;
      })
    : [];
}
